import type { BuilderProduct } from "api-types/builders/builderTypes";
import type { ClientBuilder } from "api-types/builders/clientBuilderTypes";
import type { ResourceScopedPermissionResourceType } from "api-types/builderUsers/builderUsersBaseTypes";
import type { ClientBuilderUserPermission } from "api-types/builderUsers/builderUsersResponses";
import { homePlanLockableForSubmit } from "api-types/homePlans/homePlanLockEligibility";
import type {
  ClientCascadeLockPreview,
  ClientHomePlan,
  ClientHomePlanHydratedNoDoc,
} from "api-types/homePlans/homePlanResponses";
import type { ClientLocationWithLevel } from "api-types/locations/locationBaseTypes";
import { isMultiUnitLocation } from "api-types/locations/locationDisplay";
import type { FeatureName, FeatureSet } from "common/constants/features";
import { errorPath } from "lib/routeHelpers";
import { useUserContext, type UserContextState } from "lib/state/UserContextProvider";
import { useEffect } from "react";
import { useAppNavigate } from "ui/hooks/useAppNavigate";
import { productEnabled, userIsSuperAdmin } from "./shared";

export function checkPermissions(
  hasPermission: (permission: ClientBuilderUserPermission) => boolean,
  permissions: ClientBuilderUserPermission[],
  booleanOperator: "AND" | "OR",
) {
  let allowedToView = false;

  // If no permissions checked, allow access
  if (!permissions || permissions.length === 0) {
    allowedToView = true;
  } else if (booleanOperator === "OR") {
    for (const permission of permissions) {
      if (hasPermission(permission)) {
        allowedToView = true;
        break;
      }
    }
  } else if (booleanOperator === "AND") {
    allowedToView = true;
    for (const permission of permissions) {
      if (!hasPermission(permission)) {
        allowedToView = false;
        break;
      }
    }
  }

  return allowedToView;
}

/**
 * Redirects the user to a 403 page if they lack permissions.
 *
 * Permissions are checked on an AND basis by default -
 * possessing any listed permission will grant access.
 */
export function usePermissions(
  loading: boolean,
  permissions: ClientBuilderUserPermission[] = [],
  booleanOperator: "AND" | "OR" = "AND",
  resource?: {
    resourceId: string;
    resourceType: ResourceScopedPermissionResourceType;
  },
): void {
  const navigate = useAppNavigate();
  const message = "Your account is not allowed to access this resource.";
  const { hasPermission } = useUserContext();

  const hasPermissionWithOptionalResource = (permission: ClientBuilderUserPermission) => {
    if (resource?.resourceId) {
      return hasPermission(permission, { resourceId: resource.resourceId });
    }

    return hasPermission(permission);
  };

  // Depend on a stable string key so callers can pass an inline array
  // without re-firing the effect every render.
  const permissionsKey = permissions.join("|");

  useEffect(() => {
    if (!loading && !checkPermissions(hasPermissionWithOptionalResource, permissions, booleanOperator)) {
      void navigate({ to: errorPath(403, undefined, message) });
    }
  }, [loading, permissionsKey, booleanOperator, hasPermission, resource?.resourceId, navigate, message]);
}

export function checkFeatures(enabledFeatures: FeatureSet, features?: FeatureName[] | undefined): boolean {
  if (!features || features.length === 0) {
    return true;
  }
  return features.every((feature) => !!enabledFeatures[feature]);
}

/**
 * Guard access to a component based on the setting of a number of
 * feature flags. All flags must be enabled for the check to pass.
 *
 */
export function useFeatures(
  loading: boolean,
  features: FeatureName[] | undefined,
  redirectUrl?: string,
  errorMessage?: string,
): void {
  const navigate = useAppNavigate();
  const { enabledFeatures } = useUserContext();
  const message = errorMessage || "This feature is not enabled for your account.";

  // Stable key for effect dependency
  const featuresKey = features?.join("|") ?? "";

  useEffect(() => {
    if (!loading && !checkFeatures(enabledFeatures, features)) {
      void navigate({ to: errorPath(403, redirectUrl, message) });
    }
  }, [enabledFeatures, loading, featuresKey, navigate, redirectUrl, message]);
}

export function checkProducts(builder?: ClientBuilder | undefined, products?: BuilderProduct[] | undefined): boolean {
  if (builder && products && products.length > 0) {
    for (const product of products) {
      const allowedToView = productEnabled(builder, product);

      if (!allowedToView) {
        return false;
      }
    }
  }

  return true;
}
/**
 * Guard access to a component based on the setting of any given
 * product in the productsEnabled object on a builder.
 *
 * NOTE: useProducts has the potential to be used in
 * non-authenticated routes, therefore they should not rely on the data
 * provided by the UserContextProvider, i.e. the authenticated user and builder.
 */
export function useProducts(
  builder: ClientBuilder | undefined,
  loading: boolean,
  products: BuilderProduct[] | undefined,
  redirectUrl?: string,
  errorMessage?: string,
): void {
  const navigate = useAppNavigate();
  const message = errorMessage || "The product you are trying to access is not enabled for this organization.";

  // Depend on a stable string key so callers can pass an inline array
  // without re-firing the effect every render.
  const productsKey = products?.join("|") ?? "";

  useEffect(() => {
    if (!loading && !checkProducts(builder, products)) {
      void navigate({ to: errorPath(403, redirectUrl, message) });
    }
  }, [loading, builder, productsKey, navigate, redirectUrl, message]);
}

/**
 * Determine if a user is allowed to edit a Home Plan.
 *
 * 1. Managers, Admins, and Super Admins can always edit plans.
 * 2. If a Sales Agent is assigned to a plan, they can edit it.
 * 3. If no Sales Agent is assigned to a plan, the Sales Agent for the location can edit the plan.
 * 4. Otherwise, nobody else can edit the plan.
 */
export function userCanEditHomePlan(
  { hasPermission, user }: Pick<UserContextState, "user" | "hasPermission">,
  homePlan?: Pick<ClientHomePlanHydratedNoDoc, "salesStaff">,
  location?: Pick<ClientLocationWithLevel, "salesStaffId">,
): boolean {
  let canEditHomePlan = hasPermission("EDIT_HOME_PLANS");

  if (homePlan?.salesStaff?.id) {
    if (homePlan.salesStaff.id === user?.id) {
      canEditHomePlan = true;
    }
  } else if (location?.salesStaffId) {
    if (location.salesStaffId === user?.id) {
      canEditHomePlan = true;
    }
  }

  return canEditHomePlan;
}

/** Mirrors server lock rules in planService.updateHomePlan. */
export function userCanLockHomePlan(
  { hasPermission, user }: Pick<UserContextState, "user" | "hasPermission">,
  homePlan?: Pick<ClientHomePlanHydratedNoDoc, "salesStaff" | "planStatus" | "lockedAt" | "isMultiUnit" | "lot">,
  location?: ClientLocationWithLevel,
  cascadeLockPreview?: Pick<ClientCascadeLockPreview, "lockable">,
): boolean {
  const atMultiUnitLocation = isMultiUnitLocation(location);
  return (
    userCanEditHomePlan({ hasPermission, user }, homePlan, location) &&
    homePlanLockableForSubmit({
      planStatus: homePlan?.planStatus ?? "PREP",
      isMultiUnit: !!homePlan?.isMultiUnit,
      atMultiUnitLocation,
      lotStatus: homePlan?.lot?.lotStatus,
      cascadeLockPreview,
    }) &&
    !homePlan?.lockedAt
  );
}

/** True when a unit home plan cannot unlock because its building aggregate is still locked. */
export function unitUnlockBlockedWhileBuildingLocked(
  homePlan?: Pick<ClientHomePlanHydratedNoDoc, "isMultiUnit" | "unitSource">,
): boolean {
  if (homePlan?.isMultiUnit) {
    return false;
  }
  return !!homePlan?.unitSource?.buildingHomePlanLockedAt;
}

/** Mirrors server unlock rules in planService.updateHomePlan. */
export function userCanUnlockHomePlan(
  { hasPermission, user }: Pick<UserContextState, "user" | "hasPermission">,
  homePlan?: Pick<ClientHomePlanHydratedNoDoc, "salesStaff" | "lockedAt" | "isMultiUnit" | "unitSource">,
  location?: ClientLocationWithLevel,
): boolean {
  return (
    userCanEditHomePlan({ hasPermission, user }, homePlan, location) &&
    !!homePlan?.lockedAt &&
    !unitUnlockBlockedWhileBuildingLocked(homePlan)
  );
}

/**
 * Determine if a user can detach a Home Plan. Detach is irreversible, so it requires the
 * dedicated `DETACH_HOME_PLAN` permission on top of edit access, and is unavailable once the
 * plan is locked or already detached.
 */
export function userCanDetachHomePlan(
  { hasPermission, user }: Pick<UserContextState, "user" | "hasPermission">,
  homePlan?: Pick<ClientHomePlanHydratedNoDoc, "salesStaff" | "lockedAt" | "detachedAt">,
  location?: ClientLocationWithLevel,
): boolean {
  return (
    userCanEditHomePlan({ hasPermission, user }, homePlan, location) &&
    hasPermission("DETACH_HOME_PLAN") &&
    !homePlan?.lockedAt &&
    !homePlan?.detachedAt
  );
}

export function userCanViewAutoRenders({
  hasPermission,
  builder,
  enabledFeatures,
}: Pick<UserContextState, "hasPermission" | "builder" | "enabledFeatures">): boolean {
  return !!(
    enabledFeatures.autoRenders &&
    hasPermission("VIEW_AUTO_RENDERS") &&
    productEnabled(builder, "autoRenders")
  );
}

export function userCanCreateAutoRenders({
  hasPermission,
  builder,
  enabledFeatures,
}: Pick<UserContextState, "hasPermission" | "builder" | "enabledFeatures">): boolean {
  return !!(
    enabledFeatures.autoRenders &&
    hasPermission("CREATE_AUTO_RENDERS") &&
    productEnabled(builder, "autoRenders")
  );
}

/**
 * Ensure user has permissions to use studio and that studio is enabled for the builder.
 */
export function userCanUseStudio({
  hasPermission,
  user,
  builder,
}: Pick<UserContextState, "user" | "hasPermission" | "builder">): boolean {
  // In some cases (like in studio) the builder will be missing but
  // we still want to allow super admins to access the feature
  if (!builder && userIsSuperAdmin(user)) {
    return true;
  }

  return hasPermission("VIEW_STUDIO_FILES") && productEnabled(builder, "studio");
}

/**
 * Ensure user has permissions to create/edit a studio doc
 * and that studio is enabled for the builder.
 */
export function userCanEditStudioFiles({
  hasPermission,
  user,
  builder,
}: Pick<UserContextState, "user" | "hasPermission" | "builder">): boolean {
  // In some cases (like in studio) the builder will be missing but
  // we still want to allow super admins to access the feature
  if (!builder && userIsSuperAdmin(user)) {
    return true;
  }

  return hasPermission("EDIT_STUDIO_FILES") && productEnabled(builder, "studio");
}

/**
 * Determines if a user can move a Home Plan to a different Location Plan.
 * The user must have permission to assign Home Plans to Communities and the Home Plan must not be detached.
 *
 * @param user - The user attempting to move the Home Plan.
 * @param builderUser - The Builder User associated with the Home Plan.
 * @param homePlan - The Home Plan being moved.
 *
 * @returns A tuple where the first element is a boolean indicating if the user can move the Home Plan,
 *          and the second element is a string message indicating the reason if they cannot.
 */
export function userCanMoveHomePlan(
  { hasPermission, user }: Pick<UserContextState, "user" | "hasPermission">,
  homePlan: ClientHomePlan,
  context: "CUSTOMER_PLANS" | "HOME_PLANS",
): [boolean, string] {
  if (!user) {
    return [false, "User not found"];
  }

  if (homePlan.detachedAt) {
    return [false, "Home Plan is detached and cannot be moved."];
  }

  if (context === "CUSTOMER_PLANS" && !hasPermission("MOVE_CUSTOMER_PLAN")) {
    return [false, "You do not have permission to assign customer-submitted Home Plans to Locations."];
  }

  if (!hasPermission("MOVE_HOME_PLAN")) {
    return [false, "You do not have permission to move Home Plans."];
  }

  return [true, ""];
}

export function canCreateMasterPlan(
  { hasPermission }: Pick<UserContextState, "hasPermission">,
  file: { publishedAt: Date | null },
): boolean {
  return file.publishedAt !== null && hasPermission("EDIT_MASTER_PLANS");
}

export function isCreateHomePlanModalPermitted({
  hasPermission,
  user,
  builder,
}: Pick<UserContextState, "hasPermission" | "user" | "builder">) {
  if (user && builder) {
    return hasPermission("CREATE_HOME_PLANS");
  }

  return false;
}
